import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AtSign, Eye, EyeOff, Save, Send } from 'lucide-react'
import { smtpSettingsApi, type SmtpSettings, type SmtpSettingsPayload } from '../../lib/api'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { formatDatetime } from '../../lib/utils'
import { tx } from '../../lib/i18n'

type Form = Omit<SmtpSettingsPayload, 'clearPassword'> & { pass: string }

const EMPTY: Form = { enabled: false, host: '', port: 465, secure: true, user: '', pass: '', from: '' }

const PRESETS: { label: string; host: string; port: number; secure: boolean }[] = [
  { label: 'Gmail / Google Workspace', host: 'smtp.gmail.com', port: 465, secure: true },
  { label: 'Microsoft 365 / Outlook', host: 'smtp.office365.com', port: 587, secure: false },
  { label: 'Zoho Mail', host: 'smtp.zoho.com', port: 465, secure: true },
]

const inputCls =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errorOf = (err: any, fallback: string): string => err?.response?.data?.error ?? fallback

/**
 * Platform SMTP server — queza-core sends sign-up verification and 2-step sign-in codes through it.
 * Saved in admin.system_settings (`smtp_config`); core picks up changes within ~30 s and falls back to
 * its SMTP_* env vars while this is disabled. The password is write-only (never sent back to the browser).
 */
const SmtpSettingsCard: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()
  const [form, setForm] = useState<Form>(EMPTY)
  const [showPass, setShowPass] = useState(false)
  const [testTo, setTestTo] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['system-smtp'],
    queryFn: () => smtpSettingsApi.get().then((r) => r.data.data as SmtpSettings),
  })

  useEffect(() => {
    if (data)
      setForm({
        enabled: data.enabled,
        host: data.host,
        port: data.port,
        secure: data.secure,
        user: data.user,
        from: data.from,
        pass: '',
      })
  }, [data])

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((f) => ({ ...f, [key]: value }))

  const payload = (): SmtpSettingsPayload => ({
    enabled: form.enabled,
    host: form.host.trim(),
    port: Number(form.port) || 465,
    secure: form.secure,
    user: form.user.trim(),
    from: form.from.trim(),
    ...(form.pass ? { pass: form.pass } : {}),
  })

  const saveMutation = useMutation({
    mutationFn: () => smtpSettingsApi.update(payload()),
    onSuccess: (res) => {
      queryClient.setQueryData(['system-smtp'], res.data.data)
      setForm((f) => ({ ...f, pass: '' }))
      showToast({ type: 'success', message: tx('SMTP settings saved — queza-core will use them within 30 seconds.') })
    },
    onError: (err) => showToast({ type: 'error', message: errorOf(err, 'Failed to save SMTP settings.') }),
  })

  const testMutation = useMutation({
    mutationFn: () => smtpSettingsApi.test({ ...payload(), to: testTo.trim() }),
    onSuccess: () => showToast({ type: 'success', message: `Test email sent to ${testTo.trim()}` }),
    onError: (err) => showToast({ type: 'error', message: errorOf(err, 'SMTP test failed.') }),
  })

  if (isLoading) {
    return <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-sm text-gray-400">{tx('Loading SMTP settings…')}</div>
  }

  const hasPassword = data?.hasPassword ?? false

  return (
    <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <AtSign className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Email (SMTP)</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              เซิร์ฟเวอร์อีเมลที่ระบบ Queza ใช้ส่งอีเมลยืนยันสมัคร และรหัส 2FA ตอนเข้าสู่ระบบ
            </p>
          </div>
        </div>
        <button
          onClick={() => set('enabled', !form.enabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none flex-shrink-0 ${
            form.enabled ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
          title={form.enabled ? tx('Enabled') : tx('Disabled')}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
              form.enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {error ? (
        <p className="text-sm text-red-600 mb-3">{errorOf(error, 'Failed to load SMTP settings.')}</p>
      ) : null}

      {!form.enabled && (
        <p className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          ปิดอยู่ — queza-core จะใช้ค่า SMTP_* จาก env ของเซิร์ฟเวอร์แทน (ถ้ามี) เปิดสวิตช์เพื่อใช้ค่าจากหน้านี้
        </p>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {PRESETS.map((p) => (
          <button
            key={p.host}
            type="button"
            onClick={() => setForm((f) => ({ ...f, host: p.host, port: p.port, secure: p.secure }))}
            className="px-3 py-1 text-xs rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50"
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host</label>
          <input className={inputCls} value={form.host} onChange={(e) => set('host', e.target.value)} placeholder="smtp.gmail.com" />
        </div>
        <div className="md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
          <input
            className={inputCls}
            type="number"
            min={1}
            max={65535}
            value={form.port}
            onChange={(e) => {
              const port = parseInt(e.target.value) || 0
              setForm((f) => ({ ...f, port, secure: port === 465 ? true : port === 587 || port === 25 ? false : f.secure }))
            }}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Security')}</label>
          <select className={inputCls} value={form.secure ? 'ssl' : 'starttls'} onChange={(e) => set('secure', e.target.value === 'ssl')}>
            <option value="ssl">SSL/TLS (port 465)</option>
            <option value="starttls">STARTTLS (port 587)</option>
          </select>
        </div>

        <div className="md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Username (SMTP_USER)</label>
          <input
            className={inputCls}
            value={form.user}
            onChange={(e) => set('user', e.target.value)}
            placeholder="no-reply@queza.online"
            autoComplete="off"
          />
        </div>
        <div className="md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Password (SMTP_PASS)</label>
          <div className="relative">
            <input
              className={`${inputCls} pr-10`}
              type={showPass ? 'text' : 'password'}
              value={form.pass}
              onChange={(e) => set('pass', e.target.value)}
              placeholder={hasPassword ? '•••••••• บันทึกไว้แล้ว — เว้นว่างเพื่อใช้ค่าเดิม' : 'App password'}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPass((v) => !v)}
              className="absolute inset-y-0 right-0 px-3 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">Gmail ต้องใช้ App Password (เปิด 2-Step Verification ในบัญชี Google ก่อน)</p>
        </div>

        <div className="md:col-span-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">From (SMTP_FROM)</label>
          <input
            className={inputCls}
            value={form.from}
            onChange={(e) => set('from', e.target.value)}
            placeholder="Queza <no-reply@queza.online>"
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-end gap-3 mt-5 pt-4 border-t border-gray-100">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">ส่งอีเมลทดสอบไปที่</label>
          <input
            className={inputCls}
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
          />
        </div>
        {can('system-settings.update') && (
        <button
          onClick={() => testMutation.mutate()}
          disabled={testMutation.isPending || !testTo.trim()}
          className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          {testMutation.isPending ? tx('Sending…') : tx('Send test')}
        </button>
        )}
        {can('system-settings.update') && (
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {saveMutation.isPending ? tx('Saving…') : tx('Save SMTP')}
        </button>
        )}
      </div>

      {data?.updatedAt ? (
        <p className="text-xs text-gray-400 mt-3">
          {tx('Last updated')}{data.updatedByName ? ` by ${data.updatedByName}` : ''} · {formatDatetime(data.updatedAt)}
        </p>
      ) : null}
    </div>
  )
}

export default SmtpSettingsCard
